import { createRoute, useNavigate } from '@tanstack/react-router';
import { Route as rootRoute } from './__root';
import { useState, useEffect } from 'react';
import { ShieldCheck, Lock, CreditCard, Loader2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mock-checkout',
  validateSearch: (search: Record<string, unknown>) => {
    return {
      transactionId: search.transactionId as string | undefined,
      amount: search.amount as string | undefined,
      provider: search.provider as string | undefined,
    };
  },
  component: MockCheckout,
});

function MockCheckout() {
  const { transactionId, amount, provider } = Route.useSearch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!transactionId) {
      navigate({ to: '/cart' });
    }
  }, [transactionId, navigate]);

  const handleSimulatePayment = async () => {
    setLoading(true);
    
    try {
      const res = await fetch('/api/mock-payment/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ transactionId }),
      });

      if (!res.ok) {
        throw new Error('Failed to complete mock payment');
      }

      // Simulate network delay and secure processing
      setTimeout(() => {
        setLoading(false);
        setSuccess(true);
        setTimeout(() => {
          navigate({ to: '/', search: { success: 'true', transactionId } });
        }, 1500);
      }, 1000);
    } catch (err) {
      console.error(err);
      setLoading(false);
      alert('Error completing mock payment');
    }
  };

  if (success) {
    return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center p-4 bg-neutral-50 dark:bg-neutral-900">
      <Helmet>
        <title>Mock Checkout - Cardston Market</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
        <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/20 rounded-full flex items-center justify-center mb-6 border border-emerald-200/50 dark:border-emerald-900/50">
          <ShieldCheck className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
        </div>
        <h1 className="text-2xl font-serif font-medium text-neutral-900 dark:text-white mb-2">Payment Successful</h1>
        <p className="text-neutral-500 dark:text-neutral-400">Redirecting you back to the store...</p>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center p-4 bg-neutral-50 dark:bg-neutral-900">
      <div className="max-w-md w-full bg-white dark:bg-neutral-950 rounded-2xl shadow-sm border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <div className="bg-neutral-900 dark:bg-black p-6 text-center text-white">
          <div className="flex justify-center mb-4">
            <Lock className="w-8 h-8 text-neutral-300 dark:text-neutral-500" />
          </div>
          <h1 className="text-xl font-medium tracking-tight">Secure Mock Checkout</h1>
          <p className="text-sm text-neutral-400 mt-1">Test Environment ({provider})</p>
        </div>
        
        <div className="p-8">
          <div className="flex justify-between items-center mb-8 pb-6 border-b border-neutral-100 dark:border-neutral-800">
            <span className="text-neutral-500 dark:text-neutral-400">Amount Due</span>
            <span className="text-2xl font-serif font-medium text-neutral-900 dark:text-white">
              ${amount ? (parseInt(amount) / 100).toFixed(2) : '0.00'}
            </span>
          </div>

          <div className="space-y-4 mb-8">
            <div className="bg-neutral-50 dark:bg-neutral-900 p-4 rounded-xl border border-neutral-200 dark:border-neutral-800 flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-neutral-400 dark:text-neutral-500" />
              <div className="flex-1">
                <p className="text-sm font-medium text-neutral-900 dark:text-white">Test Card</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">•••• •••• •••• 4242</p>
              </div>
            </div>
          </div>

          <button
            onClick={handleSimulatePayment}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 py-4 rounded-xl font-medium hover:bg-neutral-800 dark:hover:bg-neutral-100 transition-colors disabled:opacity-70 shadow-sm"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing Securely...
              </>
            ) : (
              <>
                <ShieldCheck className="w-5 h-5" />
                Simulate Payment
              </>
            )}
          </button>
          
          <p className="mt-6 text-xs text-center text-neutral-400 dark:text-neutral-500 flex items-center justify-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5" />
            This is a secure sandbox environment. No real funds will be charged.
          </p>
        </div>
      </div>
    </div>
  );
}
